import { FC, ReactElement, useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Login, MgtTemplateProps } from "@microsoft/mgt-react";
import { Providers, ProviderState } from "@microsoft/mgt-element";
import { Stack, Text } from "@fluentui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import mixpanel from "mixpanel-browser";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { ApiClient } from "../../network/ApiClient";
import { ApiEndpoints } from "../../network/ApiEndpoints";
import OpenFile from "../../navigation/NoobOrPro";
import "./index.css";
import { setWelcomeUser } from "../../modules/redux/WelcomeUserSlice";
import { 
  setRedirectLinksLoading, 
  setRedirectLinksSuccess
} from "../../modules/redux/RedirectLinksSlice";
import MyProfileModal from "../modals/MyProfileModal";
import { Mixpanel } from "../../modules/Mixpanel/Mixpanel";
import { getStoredRedirectId, getStoredLink } from "../../utils";
import { LOGIN_HINT_RELOGIN_KEY, shouldReloginForLoginHint } from "../../utils/loginHint";
import WelcomeModal from "../modals/WelcomeModal";
import SingleDownload from "../DownloadPopup/SingleDownload/SingleDownload";
import MultipleDownload from "../DownloadPopup/MultipleDownload/MultipleDownload";
import { Document } from "../../models";
import DownloadIcon from "../../assets/Icons/svg/downloadIcon";

const Signin: FC<any> = (): ReactElement => {
  const navigate = useNavigate();
  const [redirectLinks, setRedirectLinks] = useState<any>();
  const [params] = useSearchParams();
  const dispatch = useAppDispatch();
  const [user, setUser] = useState<any>(() => {
    try {
      const userStr = localStorage.getItem("user");
      return userStr ? JSON.parse(userStr) : null;
    } catch (e) {
      return null;
    }
  });
  const [isMyProfileModalOpen, setIsMyProfileModalOpen] = useState(false);
  const showMyProfileModal = useCallback(() => setIsMyProfileModalOpen(true), []);
  const hideMyProfileModal = () => setIsMyProfileModalOpen(false);
  const [isWelcomeModalOpen, setIsWelcomeModalOpen] = useState(false);
  const showWelcomeModal = () => setIsWelcomeModalOpen(true);
  const hideWelcomeModal = () => setIsWelcomeModalOpen(false);
  const [isDownloadPopupOpen, setIsDownloadPopupOpen] = useState(false);
  const showDownloadPopup = () => setIsDownloadPopupOpen(true);
  const hideDownloadPopup = () => setIsDownloadPopupOpen(false);
  const [isMultipleDownloadPopupOpen, setIsMultipleDownloadPopupOpen] = useState(false);
  const showMultipleDownloadPopup = () => setIsMultipleDownloadPopupOpen(true);
  const hideMultipleDownloadPopup = () => setIsMultipleDownloadPopupOpen(false);
  const [downloadData, setDownloadData] = useState<Document[]>([]);
  const [multipleDownloadData, setMultipleDownloadData] = useState<Document[]>([]);
  const [checkedDocumentsForDownload, setCheckedDocumentsForDownload] = useState<Document[]>([]);
  const welcomeUser = useAppSelector((state) => state.WelcomeUser.value);
  const loginHint = useMemo(() => params.get('loginHint')?.toLowerCase(), [params]);
  const loginHintHandled = useRef(false);

  const handleLogin = useCallback(async () => {
    try {
      await Providers.globalProvider.login!();
    } catch (error: any) {
      console.error('Error during login:', error);
    }
  }, []);

  useEffect(() => {
    if (welcomeUser === "start") {
      setIsWelcomeModalOpen(true);
    }
    else if (welcomeUser === "redirect" || welcomeUser === "close") {
      if (redirectLinks) {
        // DEX-559: capture the stored LINK before removing it — reading after removeItem
        // yielded null and `new URL(null)` threw, killing the first-login redirect download.
        const storedLink = localStorage.getItem('LINK');
        localStorage.removeItem("LINK");

        const urlParams = new URL(storedLink!).searchParams;
        const shouldDownload = urlParams.get("download") === "true";
        const isMulti = urlParams.get("multiFileDownload") === "true";
      
        if (shouldDownload || isMulti) {
          if (isMulti) {
            setMultipleDownloadData(redirectLinks.data.documents);
            setCheckedDocumentsForDownload(redirectLinks.data.documents);
            showMultipleDownloadPopup();
          } else {
            setDownloadData(redirectLinks.data.documents);
            showDownloadPopup();
          }
        } else {
          OpenFile(redirectLinks.data.desktopUrl, redirectLinks.data.webUrl);
        }
      } else {
        // Since permissions are set in the welcome modal, we refresh the page to ensure the user has access to everything
        window.location.reload();
      }
    }
  }, [welcomeUser, redirectLinks]);

  useEffect(() => {
    if (!loginHint || loginHintHandled.current) return;

    loginHintHandled.current = true;
    localStorage.setItem('LINK', window.location.href);

    const accountCount = Providers.globalProvider.getAllAccounts!().length;
    let activeEmail: string | undefined;
    if (accountCount > 0) {
      try {
        activeEmail = Providers.globalProvider.getActiveAccount!().mail;
      } catch (error) {
        console.error('Error getting active account:', error);
      }
    }

    // The hint is the address the share was sent to; a guest's sign-in name often differs from it, and
    // re-login returns to the same account, so redirecting on every mismatch loops. Offer it once per tab.
    const relogin = shouldReloginForLoginHint({
      loginHint,
      accountCount,
      activeEmail,
      storedUserEmail: user?.email,
      reloginOfferedFor: sessionStorage.getItem(LOGIN_HINT_RELOGIN_KEY),
    });
    if (relogin) {
      sessionStorage.setItem(LOGIN_HINT_RELOGIN_KEY, loginHint);
      handleLogin();
    }
  }, [loginHint, handleLogin, user]);

  const openFileFromLink = useCallback(async (redirect_id: any, isFirstLogin: boolean) => {    
    let links;
    // Note: redirectLinks in closure might be stale, but we check if we need to fetch
    // Use functional update or ref if critical, but here logic relies on whether we HAVE links
    // Since we setRedirectLinks below, next render will have them.
    
    // Check local variable 'links' or state 'redirectLinks'
    // This logic seems to assume if we don't have links yet, fetch them.
    
    if (!redirectLinks && redirect_id) {
      dispatch(setRedirectLinksLoading());
      links = await ApiClient.get<any>(
        ApiEndpoints.getDocumentLinks(),
        {
          encryptedRedirectId: redirect_id,
        },
        {}
      );
      dispatch(setRedirectLinksSuccess(links));
      setRedirectLinks(links);
    }

    // The useEffect above will handle the file opening once the user closes the welcome modal
    if (isFirstLogin) return;

    const linkData = links?.data || redirectLinks?.data;
    
    if (linkData) {
      const urlParams = new URL(localStorage.getItem('LINK')!).searchParams;
      const shouldDownload = urlParams.get("download") === "true";
      const isMulti = urlParams.get("multiFileDownload") === "true";
    
      if (shouldDownload || isMulti) {
        if (isMulti) {
          setMultipleDownloadData(linkData.documents);
          setCheckedDocumentsForDownload(linkData.documents);
          showMultipleDownloadPopup();
        } else {
          setDownloadData(linkData.documents);
          showDownloadPopup();
        }
      } else{
        OpenFile(linkData.desktopUrl, linkData.webUrl);
      }
    } else {
      navigate("pagenotfound");
    }

    localStorage.removeItem("LINK");
  }, [redirectLinks, dispatch, navigate]);

  const handleRedirection = useCallback(async (isFirstLogin: boolean) => {
    const redirectId = getStoredRedirectId();
    const storedLink = getStoredLink();
    
    if (redirectId) {
      await openFileFromLink(redirectId, isFirstLogin);
    } else if (storedLink) {
      localStorage.removeItem('LINK');
      navigate(storedLink);
    }
  }, [openFileFromLink, navigate]);

  const trackUserLogin = useCallback((user: any) => {
    Mixpanel.identify(user.id);
    Mixpanel.alias(user.id);
    mixpanel.track('Successful login');
    Mixpanel.people.set({
      $email: user.email,
      $first_name: user.firstName,
      $last_name: user.lastName,
    });
  }, []);

  const loginCompleted = useCallback(async () => {
    if (Providers.globalProvider.state !== ProviderState.SignedIn) return;
    
    try {
      const { data: userData } = await ApiClient.get<any>(ApiEndpoints.userDetails());
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.removeItem('loginHint');
      setUser(userData);

      trackUserLogin(userData);

      const isFirstLogin = userData?.isFirstTimeLogin ?? false;

      if (isFirstLogin) {
        dispatch(setWelcomeUser("start"));
      }

      await handleRedirection(isFirstLogin);     
    } catch (error) {
      console.error('Error during login completion:', error);
    }
  }, [trackUserLogin, dispatch, handleRedirection]);

  const loginInitiated = useCallback(() => {
    localStorage.removeItem('loginHint');
  }, []);

  const logoutCompleted = useCallback(() => {
    localStorage.removeItem('loginHint');
    sessionStorage.removeItem(LOGIN_HINT_RELOGIN_KEY);
    sessionStorage.removeItem('Access-Token');
  }, []);

  const loginFailed = useCallback((error: any) => {
    console.error('Login Failed:', error);
    Mixpanel.track('Unsuccessful login');
  }, []);

  const CustomTemplate = useCallback((props: MgtTemplateProps) => {
    const capitalizeFirstLetter = (str: any) => {
      return str?.charAt(0).toUpperCase() + str?.slice(1);
    };

    const firstNameCapitalized =
      user?.firstName && capitalizeFirstLetter(user?.firstName);
    const emailCapitalized = user?.email && capitalizeFirstLetter(user?.email);

    return (
      <Stack
        style={{
          width: "100%",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
        }}
      >
        <div
          style={{
            width: "60px",
            height: "60px",
            borderRadius: "40px",
            backgroundColor: "#F8F8F8",
            padding: "23px",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text
            style={{
              fontSize: "24px",
              lineHeight: "16px",
              fontWeight: 400,
              fontFamily: "Poppins",
              color: "#323232",
            }}
          >
            {firstNameCapitalized?.charAt(0)}
          </Text>
        </div>

        <Stack
          style={{
            fontSize: "15px",
            lineHeight: "16px",
            fontWeight: 500,
            fontFamily: "Poppins",
            color: "#18468F",
            cursor: "pointer",
          }}
          onClick={() => {
            showMyProfileModal();
          }}
        >
          {firstNameCapitalized}
        </Stack>

        <Text
          style={{
            fontSize: "14px",
            lineHeight: "16px",
            fontWeight: 400,
            fontFamily: "Poppins",
            color: "#717070",
            display: "block",
            whiteSpace: "nowrap",
          }}
        >
          {emailCapitalized}
        </Text>
      </Stack>
    );
  }, [user, showMyProfileModal]);

  return (
    <>
      <Login
        id="loginButton"
        loginCompleted={loginCompleted}
        logoutCompleted={logoutCompleted}
        loginInitiated={loginInitiated}
        loginFailed={loginFailed}
        className="login"
      >
        <CustomTemplate template="flyout-person-details" />
      </Login>

      <MyProfileModal
        isModalOpen={isMyProfileModalOpen}
        hideModal={hideMyProfileModal}
        showModal={showMyProfileModal}
      />

      <WelcomeModal
        isModalOpen={isWelcomeModalOpen}
        hideModal={hideWelcomeModal}
        showModal={showWelcomeModal}
      />

      <SingleDownload
        isModalOpen={isDownloadPopupOpen}
        hideModal={hideDownloadPopup}
        showModal={showDownloadPopup}
        documents={downloadData}
      />

      <MultipleDownload
        isModalOpen={isMultipleDownloadPopupOpen}
        hideModal={hideMultipleDownloadPopup}
        showModal={showMultipleDownloadPopup}
        selectedDocuments={checkedDocumentsForDownload}
        setSelectedDocuments={setCheckedDocumentsForDownload}
        documents={multipleDownloadData}
        icon={<DownloadIcon />}
        warningTitle="Are you sure you want to download the following documents?"
        warningDescription="Changes made to downloaded documents offline will not be saved in Dexterity.  "
      />
    </>
  );
};

export default Signin;
